import { useEffect, useRef, useState, type FocusEventHandler } from 'react';
import { InputText } from '@ev-tech/ui-core-components/InputText';

import { useAppContext } from '../../hooks/useAppContext';

import styles from './SearchAlert.module.css';

const DISALLOWED_CHARS = ['<', ';', '>', '/', '*'];
const disallowedCharsPattern = `[^${DISALLOWED_CHARS.join('\\')}]*`;

export type NameInputProps = {
  autoFocus?: boolean;
  defaultValue?: string;
  label?: string;
  onBlur?: FocusEventHandler<HTMLInputElement>;
};

export const NameInput = ({ autoFocus, defaultValue, label: labelProp, onBlur }: NameInputProps) => {
  const { t } = useAppContext();
  const [validity, setValidity] = useState<Partial<ValidityState>>({
    valid: true,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const label = labelProp || t('search.searchAlert.create.name.label');

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const updateValidity = () => {
    if (!inputRef.current) return;
    const { valid, patternMismatch, valueMissing } = inputRef.current.validity;
    setValidity({ valid, patternMismatch, valueMissing });
  };

  const errorMessage = () => {
    if (validity.patternMismatch)
      return t('search.searchAlert.create.name.notAllowed', {
        characters: DISALLOWED_CHARS.join(' '),
      });
    if (validity.valueMissing) return t('search.searchAlert.create.name.required');
    return '';
  };

  return (
    <div className={styles.inputWrapper}>
      <InputText
        defaultValue={defaultValue}
        label={label}
        name="name"
        isRequired
        size="m"
        pattern={disallowedCharsPattern}
        ref={inputRef}
        error={!validity.valid}
        helperText={errorMessage()}
        data-testid="search-components_search-alert-creation-form_name-input"
        onChange={updateValidity}
        onBlur={(event) => {
          onBlur?.(event);
          updateValidity();
        }}
      />
    </div>
  );
};
